import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useStacklineTheme } from '@stackline/ui';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import {
  parseCumulativeProductCatalogCountRatingVolumeResponse,
  useProductCatalogCountRatingData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { EntityCategoryDatum } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useAppSelector } from 'src/utils/Hooks';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' }
];

// For 400 and Above xAxis, we are not able to wrap it with css.
export const CustomXAxisLabel = ({ xAxisCategory }: { xAxisCategory: EntityCategoryDatum }) => {
  //       400         and         Above
  const [firstWord, secondWord, thirdWord] = xAxisCategory?.name?.split(' ');

  return (
    <div
      style={{
        fontSize: '12px',
        textAlign: 'center',
        width: '70px',
        wordBreak: 'break-word',
        maxHeight: '70px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      <p style={{ marginBottom: 0 }} title={xAxisCategory.name}>
        {firstWord}
      </p>
      <p style={{ marginTop: 0 }} title={xAxisCategory.name}>
        {`${secondWord} ${thirdWord}`}
      </p>
    </div>
  );
};

/**
 * Product Catalog Count by Cumulative Rating Volume Chart for Ratings tab.
 *
 */
const ProductCatalogCountRatingVolumeChart = () => {
  const theme = useStacklineTheme();
  const [selectedMetric, setSelectedMetric] = useState('cumulativeProductCatalogCount');
  const normalizedSelectedMetric = productCatalogCountOptions.some((option) => option.id === selectedMetric)
    ? selectedMetric
    : 'cumulativeProductCatalogCount';
  const shouldShowMetricDropdown = productCatalogCountOptions.length > 1;
  const selectedMetricLabel =
    productCatalogCountOptions.find((option) => option.id === normalizedSelectedMetric)?.label ||
    productCatalogCountOptions[0].label;
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { data, isLoading: cumulativeLoading } = useProductCatalogCountRatingData(
    RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME
  );

  const { currentData } = data
    ? parseCumulativeProductCatalogCountRatingVolumeResponse({
        data: data as any,
        comparisonEndWeek,
        metricName: 'Ratings'
      })
    : { currentData: [] };
  const chartData = currentData as [string, number][];

  return (
    <BeaconChartWithLegend
      title={`${selectedMetricLabel} Rating Volume Tier`}
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveSingleBarChartAsDelimitedData(chartData, { metricLabel: 'Product Catalog Count' });
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each rating volume tier.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={
        shouldShowMetricDropdown
          ? {
              defaultLabel: '',
              options: productCatalogCountOptions,
              selectedId: normalizedSelectedMetric,
              onChange: (option) => setSelectedMetric(option.id),
              suffix: 'Rating Volume Tier'
            }
          : undefined
      }
      primaryLegendProps={{
        displayName: legendLabel
      }}
      loading={cumulativeLoading}
      loadingComponent={<SplineChartLoading width={1000} />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          if (xAxisCategory.name === '400 and Above') {
            return ReactDOMServer.renderToStaticMarkup(<CustomXAxisLabel xAxisCategory={xAxisCategory} />);
          }
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: chartData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
        seriesColors={[theme.colors.primary]}
      />
    </BeaconChartWithLegend>
  );
};

export default ProductCatalogCountRatingVolumeChart;
